import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { UserSummaryFragment } from "../../graphql/fragments";
import useSignReport from "../../graphql/hooks/mutations/useSignReport";
import useAddNotification from "../../hooks/useAddNotification";

import useTranslations from "../../hooks/useTranslations";

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalActions,
  Button,
  Avatar,
  Badge,
  Checkbox,
} from "../wombat-ui";
import { BadgeVariant } from "../wombat-ui/components/Badge";

function getBadgeColorByRole(
  madRole: "talent" | "referent" | "supervisor"
): BadgeVariant {
  if (madRole === "talent") return "primary";
  if (madRole === "referent") return "warning";
  if (madRole === "supervisor") return "help";
}

type ReportSignatureModalProps = {
  open: boolean;
  onClose: () => void;
  reportId: string;
  signatories: (UserSummaryFragment & {
    madRole: "talent" | "referent" | "supervisor";
  })[];
  generateDocument: (
    pageSize: "A4" | "LETTER",
    directDownload: boolean,
    signatories?: string[]
  ) => Promise<{
    document: string;
    filename: string;
  }>;
};

type SignatoriesData = {
  signatories: string[];
};

const ReportSignatureModal: React.FC<ReportSignatureModalProps> = ({
  open,
  onClose,
  reportId,
  signatories,
  generateDocument,
}) => {
  const t = useTranslations();

  const { register, handleSubmit } = useForm<SignatoriesData>({
    defaultValues: { signatories: signatories.map(({ id }) => id) },
  });

  const [signReport, { loading: signLoading }] = useSignReport();
  const [addNotification] = useAddNotification();

  const onSubmit = useCallback(
    async (data: SignatoriesData) => {
      const document = await generateDocument("A4", false, data.signatories);

      try {
        await await signReport({
          variables: {
            input: {
              reportId: reportId,
              signatoryIds: data.signatories,
              ...document,
            },
          },
        });

        addNotification({
          variables: {
            title: t(`reports.signature.notification.success`) as string,
            body: t(`reports.signature.notification.signatureAsked`) as string,
            status: "success",
          },
        });
        onClose();
      } catch (error) {
        addNotification({
          variables: {
            title: t(`userProfile.notification.error`) as string,
            status: "error",
          },
        });
      }
    },
    [signReport, reportId, addNotification]
  );

  return (
    <Modal isOpen={open} aria-label="Sign report" id={`signModal`}>
      <ModalHeader onClose={onClose}>
        {t("reports.signature.header")}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)} id="report-sign-form">
          {t("reports.signature.selectSignatories")}
          <div className="max-w-sm divide-y divide-gray-200">
            {signatories.map((signatory) => {
              return (
                <label className="flex justify-between py-4" key={signatory.id}>
                  <div className="flex flex-grow">
                    <Avatar
                      size={10}
                      src={signatory?.avatar}
                      alt={signatory?.name}
                      name={signatory?.name}
                    />
                    <div className="ml-3">
                      <p className="flex space-x-3 text-sm font-medium text-gray-900">
                        {signatory?.name}
                      </p>
                      <Badge
                        variant={getBadgeColorByRole(signatory.madRole)}
                        scale="sm"
                      >
                        {t(
                          `reports.signature.role.${signatory.madRole}` as const
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <Checkbox
                      label={`Define ${signatory.name} as signatory`}
                      srOnlyLabel
                      id={`signatory-${signatory.id}`}
                      name={"signatories"}
                      className="text-blue-600 rounded-sm"
                      value={signatory.id}
                      ref={register}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        </form>
      </ModalBody>
      <ModalActions>
        <Button
          variant="subtle"
          onClick={onClose}
          id={`updateTemplateWithoutSavingButton`}
        >
          {t("forms.actions.cancel")}
        </Button>
        <Button
          loading={signLoading}
          variant="primary"
          type="submit"
          form="report-sign-form"
          id={`updateTemplateWithSavingButton`}
        >
          {t("forms.actions.send")}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default React.memo(ReportSignatureModal);
